import { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, Settings, X, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Convert base64 to Uint8Array for push subscription
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function NotificationManager({ selectedRegion }) {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    price_alerts: true,
    tomorrow_alerts: true,
    persistent_summary: false,
    threshold: 50,
    region: selectedRegion || 'peninsula'
  });

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      checkSubscription();
    }
  }, []);

  // Update region in settings when it changes
  useEffect(() => {
    setSettings(prev => ({ ...prev, region: selectedRegion }));
  }, [selectedRegion]);

  const checkSubscription = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
      
      // Load saved settings from localStorage
      const savedSettings = localStorage.getItem('notificationSettings');
      if (savedSettings) {
        setSettings(JSON.parse(savedSettings));
      }
    } catch (e) {
      console.error('Error checking subscription:', e);
    }
  }, []);

  const subscribe = async () => {
    setLoading(true);
    try {
      // Get VAPID public key from server
      const keyResponse = await fetch(`${API}/notifications/vapid-key`);
      const { publicKey } = await keyResponse.json();
      
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        alert('Necesitas permitir las notificaciones para activar las alertas.');
        setLoading(false);
        return;
      }
      
      // Subscribe to push
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
      
      // Send subscription to server
      const response = await fetch(`${API}/notifications/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          settings: settings
        })
      });
      
      if (response.ok) {
        setIsSubscribed(true);
        localStorage.setItem('notificationSettings', JSON.stringify(settings));
        // Track in Umami
        if (window.umami) {
          window.umami.track('push-notifications-enabled');
        }
      }
    } catch (e) {
      console.error('Error subscribing:', e);
      alert('Error al activar las notificaciones. Inténtalo de nuevo.');
    }
    setLoading(false);
  };

  const unsubscribe = async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Notify server
        await fetch(`${API}/notifications/unsubscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(subscription.toJSON())
        });
        
        // Unsubscribe locally
        await subscription.unsubscribe();
        setIsSubscribed(false);
        localStorage.removeItem('notificationSettings');
      }
    } catch (e) {
      console.error('Error unsubscribing:', e);
    }
    setLoading(false);
  };

  const updateSettings = async (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem('notificationSettings', JSON.stringify(newSettings));
    
    if (isSubscribed) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        
        if (subscription) {
          await fetch(`${API}/notifications/settings?endpoint=${encodeURIComponent(subscription.endpoint)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(newSettings)
          });
        }
      } catch (e) {
        console.error('Error updating settings:', e);
      }
    }
  };

  if (!isSupported) {
    return null;
  }

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => isSubscribed ? setShowSettings(true) : subscribe()}
        disabled={loading}
        className="rounded-full"
        data-testid="notification-toggle"
        title={isSubscribed ? 'Configurar alertas' : 'Activar alertas'}
      >
        {loading ? (
          <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : isSubscribed ? (
          <Bell className="h-5 w-5 text-primary" />
        ) : (
          <BellOff className="h-5 w-5" />
        )}
      </Button>

      {/* Settings Modal */}
      {showSettings && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowSettings(false)}
        >
          <Card 
            className="w-full max-w-sm p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Configurar Alertas
              </h3>
              <button onClick={() => setShowSettings(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Price alerts toggle */}
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm font-medium">Alertas de precio bajo</span>
                <input
                  type="checkbox"
                  checked={settings.price_alerts}
                  onChange={(e) => updateSettings({ ...settings, price_alerts: e.target.checked })}
                  className="w-5 h-5 accent-primary"
                />
              </label>

              {/* Threshold slider */}
              {settings.price_alerts && (
                <div>
                  <label className="text-sm text-muted-foreground">
                    Avisar si precio menor a: <strong>{(settings.threshold / 1000).toFixed(3)} €/kWh</strong>
                  </label>
                  <input
                    type="range"
                    min="20"
                    max="150"
                    step="10"
                    value={settings.threshold}
                    onChange={(e) => updateSettings({ ...settings, threshold: Number(e.target.value) })}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>0,02€</span>
                    <span>0,15€</span>
                  </div>
                </div>
              )}

              {/* Tomorrow alerts toggle */}
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm font-medium">Aviso precios de mañana</span>
                <input
                  type="checkbox"
                  checked={settings.tomorrow_alerts}
                  onChange={(e) => updateSettings({ ...settings, tomorrow_alerts: e.target.checked })}
                  className="w-5 h-5 accent-primary"
                />
              </label>

              {/* Persistent summary toggle */}
              <div className="border-t pt-4 mt-4">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <span className="text-sm font-medium flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Resumen cada hora
                    </span>
                    <p className="text-xs text-muted-foreground mt-1">
                      Notificación con precio actual y mejor hora
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.persistent_summary}
                    onChange={(e) => updateSettings({ ...settings, persistent_summary: e.target.checked })}
                    className="w-5 h-5 accent-primary"
                  />
                </label>
                {settings.persistent_summary && (
                  <div className="mt-2 p-2 bg-muted rounded-lg text-xs">
                    <p className="font-medium mb-1">Ejemplo de notificación:</p>
                    <p>🟢 Ahora: 0,048€/kWh (MEJOR)</p>
                    <p className="text-muted-foreground">Mejor hora hoy: 14:00 (0,035€/kWh)</p>
                  </div>
                )}
              </div>

              {/* Unsubscribe button */}
              <Button
                variant="outline"
                className="w-full mt-4 text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
                onClick={() => {
                  unsubscribe();
                  setShowSettings(false);
                }}
              >
                <BellOff className="w-4 h-4 mr-2" />
                Desactivar alertas
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}

export default NotificationManager;
